import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../../styles/DigitalWallet.css";
import DigitalWalletSearchIcon from "../../icons/DigitalWalletSearchIcon";
import DigitalWalletCalendarIcon from "../../icons/DigitalWalletCalendarIcon";
import DigitalWalletChevronDownIcon from "../../icons/DigitalWalletChevronDownIcon";
import DigitalWalletRejectIcon from "../../icons/DigitalWalletRejectIcon";
import DigitalWalletApproveIcon from "../../icons/DigitalWalletApproveIcon";
import DigitalWalletChevronIcon from "../../icons/DigitalWalletChevronIcon";
import WalletFundsModal from "./WalletFundsModal";
import DateRangeFilter from "./DateRangeFilter";
import vendorWalletController from "../../controllers/vendorWalletController";
import whishPayController from "../../controllers/whishPayController";
import { userService } from "../../services/userService";
import { translateLabel, translateText, useTranslationSnapshot } from "./../../translations/runtime";


const getStoredToken = () => {
  const storedUser = userService.getJwt();
  if (typeof storedUser === "string") return storedUser;

  return (
    storedUser?.jwt ||
    storedUser?.token ||
    storedUser?.accessToken ||
    storedUser?.access_token ||
    ""
  );
};

const getStoredCompanyId = () => {
  const payload = userService.getUserPayload() || {};
  const userDetailsCompanyId = Object.keys(payload.userDetails || {})[0];
  const userData = userService.getUserData();

  return (
    payload.companyId ||
    payload.companyID ||
    userDetailsCompanyId ||
    userData.Compid ||
    ""
  );
};

const formatMoney = (amount) => {
  const numberValue = Number(amount);
  if (Number.isNaN(numberValue)) return "$0.00";
  return `${numberValue < 0 ? "-" : ""}$${Math.abs(numberValue).toFixed(2)}`;
};

const unwrapPayload = (response) =>
  response?.data?.data ?? response?.data?.result ?? response?.data ?? {};

const getPayloadWalletId = (payload) =>
  payload?.walletId || payload?.walletID || payload?.wallet?.walletId || "";

const getWhishPaymentPayload = (payload = {}) => payload.payment || payload;

const toTitleCase = (value) =>
  String(value || "")
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatWalletDate = (value) => {
  const rawDate = value?.time ? new Date(value.time) : new Date(value || "");
  if (Number.isNaN(rawDate.getTime())) return { date: "-", time: "", iso: "" };

  return {
    date: rawDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    iso: `${rawDate.getFullYear()}-${String(rawDate.getMonth() + 1).padStart(2, "0")}-${String(rawDate.getDate()).padStart(2, "0")}`,
    time: rawDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    }),
  };
};

const mapWithdrawalRequest = (request) => {
  const formattedDate = formatWalletDate(request.date || request.createdAt);

  return {
    id: request.requestId || "-",
    transactionId: request.transactionId || "",
    name: request.name || "-",
    phone: request.phoneNumber || "-",
    type: toTitleCase(request.type || "Vendor"),
    amount: formatMoney(request.amount),
    date: formattedDate.date,
    isoDate: formattedDate.iso,
    time: formattedDate.time,
  };
};

const mapWalletTransaction = (transaction) => {
  const formattedDate = formatWalletDate(
    transaction.date || transaction.createdAt,
  );
  const amount = Number(transaction.amount) || 0;

  return {
    id: transaction.transactionId || transaction.requestId || "-",
    name: transaction.name || "-",
    phone: transaction.phoneNumber || "-",
    type: toTitleCase(transaction.type || "Vendor"),
    transactionType: toTitleCase(transaction.transactionType),
    description: transaction.description || "-",
    amount: `${amount >= 0 ? "+" : "-"}$${Math.abs(amount).toFixed(2)}`,
    date: formattedDate.date,
    isoDate: formattedDate.iso,
    time: formattedDate.time,
    status: toTitleCase(transaction.status || transaction.withdrawalStatus),
  };
};

const typeOptions = ["Vendor", "Driver"];
const transactionTypeOptions = ["Top Up", "Withdrawal"];

function DigitalWalletDropdown({
  dropdownKey,
  value,
  options,
  activeDropdown,
  onToggle,
  onSelect,
}) {
  useTranslationSnapshot();
  const isOpen = activeDropdown === dropdownKey;

  return (
    <div
      className={`digital-wallet-filter-control digital-wallet-custom-dropdown ${
        isOpen ? "digital-wallet-custom-dropdown-open" : ""
      }`}
      onClick={(event) => {
        event.stopPropagation();
        onToggle(dropdownKey);
      }}
    >
      <div>{value}</div>
      <DigitalWalletChevronDownIcon />

      {isOpen && (
        <div className="digital-wallet-dropdown-menu">
          {options.map((option, index) => (
            <div
              className={`digital-wallet-dropdown-option ${
                value === option || (!options.includes(value) && index === 0)
                  ? "digital-wallet-dropdown-option-selected"
                  : ""
              }`}
              key={option}
              onClick={(event) => {
                event.stopPropagation();
                onSelect(option);
              }}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DigitalWallet() {
  useTranslationSnapshot();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeModal, setActiveModal] = useState(null);
  const [fundsModalMode, setFundsModalMode] = useState(null);
  const [activeTab, setActiveTab] = useState("withdrawals");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [selectedType, setSelectedType] = useState("Types");
  const [selectedTransactionType, setSelectedTransactionType] =
    useState("Transaction Types");
  const [walletId, setWalletId] = useState("");
  const [walletBalance, setWalletBalance] = useState(0);
  const [withdrawalRows, setWithdrawalRows] = useState([]);
  const [transactionRows, setTransactionRows] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [dateRangeOpen, setDateRangeOpen] = useState(false);

  const closeModal = () => {
    setActiveModal(null);
    setSelectedRequest(null);
  };
  const closeFundsModal = () => setFundsModalMode(null);
  const dateRangeLabel =
    dateRange.from || dateRange.to
      ? `${dateRange.from || "Start"} - ${dateRange.to || "End"}`
      : "Date Range";
  const isWithinDateRange = (row) => {
    if (!dateRange.from && !dateRange.to) return true;
    if (!row.isoDate) return false;
    if (dateRange.from && row.isoDate < dateRange.from) return false;
    if (dateRange.to && row.isoDate > dateRange.to) return false;
    return true;
  };
  const visibleWithdrawalRows = withdrawalRows.filter(isWithinDateRange);
  const visibleTransactionRows = transactionRows.filter(isWithinDateRange);

  const loadWalletData = useCallback(async () => {
    try {
      const Authorization = getStoredToken();
      const companyId = getStoredCompanyId();

      if (!Authorization || !companyId) return;

      const walletResponse = await vendorWalletController.getWallet({
        Authorization,
        companyId,
      });
      const walletPayload = unwrapPayload(walletResponse);
      let nextWalletId = getPayloadWalletId(walletPayload);

      setWalletId(nextWalletId || "");
      setWalletBalance(
        walletPayload.cashWalletBalance || walletPayload.walletBalance || 0,
      );

      if (nextWalletId) {
        const [withdrawalsResponse, transactionsResponse] = await Promise.all([
          vendorWalletController.getWithdrawalRequests({
            Authorization,
            walletId: nextWalletId,
          }),
          vendorWalletController.getWalletTransactions({
            Authorization,
            walletId: nextWalletId,
          }),
        ]);

        const withdrawalsPayload = unwrapPayload(withdrawalsResponse);
        const transactionsPayload = unwrapPayload(transactionsResponse);
        const withdrawals = withdrawalsPayload.items || [];
        const walletTransactions = transactionsPayload.wWlletTransaction || [];

        setWithdrawalRows(withdrawals.map(mapWithdrawalRequest));
        setTransactionRows(walletTransactions.map(mapWalletTransaction));
      }
    } catch (error) {
      console.error("Wallet API error", error);
    }
  }, []);

  useEffect(() => {
    loadWalletData();
  }, [loadWalletData]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get("refreshWallet") !== "1") return;

    loadWalletData();
    params.delete("refreshWallet");
    params.delete("paymentId");
    navigate(
      {
        pathname: location.pathname,
        search: params.toString(),
      },
      { replace: true },
    );
  }, [loadWalletData, location.pathname, location.search, navigate]);

  const resolveWalletId = async (Authorization) => {
    if (walletId) return walletId;

    const companyId = getStoredCompanyId();
    if (!companyId) return "";

    const walletResponse = await vendorWalletController.getWallet({
      Authorization,
      companyId,
    });
    const walletPayload = unwrapPayload(walletResponse);
    const nextWalletId = getPayloadWalletId(walletPayload);

    if (nextWalletId) {
      setWalletId(nextWalletId);
    }

    return nextWalletId;
  };
  const handleFundsConfirm = async (amount) => {
    const Authorization = getStoredToken();
    const currentWalletId = await resolveWalletId(Authorization);
    if (!currentWalletId) return;

    const requestId = crypto.randomUUID?.() || String(Date.now());

    if (fundsModalMode !== "withdraw") {
      window.dispatchEvent(new Event("vendor-loading:start"));

      try {
        const response = await whishPayController.createWalletTopUp({
          Authorization,
          request: {
            amount,
            referenceId: requestId,
            requestId,
            walletId: currentWalletId,
          },
        });

        if (response?.isAxiosError || response?.response) {
          throw response;
        }

        const payload = unwrapPayload(response);
        const paymentPayload = getWhishPaymentPayload(payload);
        const collectUrl =
          paymentPayload.collectUrl || payload.collectUrl || "";
        const paymentId = paymentPayload.paymentId || payload.paymentId || "";

        closeFundsModal();
        navigate({
          pathname: "/wishpay",
          search: new URLSearchParams({
            amount: String(amount),
            collectUrl,
            currency: paymentPayload.currency || payload.currency || "USD",
            mode: "topup",
            paymentId,
            requestId: payload.requestId || requestId,
            returnTo: location.pathname,
            walletId:
              paymentPayload.walletId || payload.walletId || currentWalletId,
          }).toString(),
        });
      } catch (error) {
        console.error("[Whish top-up] error", {
          data: error?.response?.data,
          message: error?.message,
          status: error?.response?.status,
        });
      } finally {
        window.dispatchEvent(new Event("vendor-loading:stop"));
      }

      return;
    }

    closeFundsModal();
    navigate({
      pathname: "/wishpay",
      search: new URLSearchParams({
        amount: String(amount),
        currency: "USD",
        mode: fundsModalMode === "withdraw" ? "withdraw" : "topup",
        requestId,
        returnTo: location.pathname,
        walletId: currentWalletId,
      }).toString(),
    });
  };
  const handleWithdrawalDecision = async () => {
    const Authorization = getStoredToken();
    if (!selectedRequest?.transactionId) return;

    const request = {
      adminWalletId: walletId,
      reason: "",
      transactionId: selectedRequest.transactionId,
    };

    if (activeModal === "approve") {
      await vendorWalletController.approveWithdrawal({
        Authorization,
        request,
      });
    } else {
      await vendorWalletController.rejectWithdrawal({ Authorization, request });
    }

    closeModal();
    loadWalletData();
  };
  const toggleDropdown = (dropdownKey) => {
    setActiveDropdown((currentDropdown) =>
      currentDropdown === dropdownKey ? null : dropdownKey,
    );
  };

  const selectDropdownOption = (option, onSelect) => {
    onSelect(option);
    setActiveDropdown(null);
  };

  const modalCopy =
    activeModal === "approve"
      ? {
          title: "Approved Withdrawal Requests",
          placeholder: "Explain why this application is being approved...",
          confirmLabel: "Approve Vendor",
          tone: "approve",
        }
      : {
          title: "Rejection Withdrawal Requests",
          subtitle: "Please provide a reason for rejection.",
          label: "Rejection Reason",
          placeholder: "Explain why this application is being rejected...",
          confirmLabel: "Reject Application",
          tone: "reject",
        };

  return (
    <div
      className="digital-wallet-wrapper"
      onClick={() => setActiveDropdown(null)}
    >
      <div className="digital-wallet-header">
        <div className="digital-wallet-heading-group">
          <div className="digital-wallet-title">{translateLabel("lnk_digital_wallet", "Digital Wallet")}</div>
          <div className="digital-wallet-subtitle">
            Manage wallet balance and Withdrawal Request.
          </div>
        </div>

        <div className="digital-wallet-top-row">
          <div className="digital-wallet-balance-card">
            <div className="digital-wallet-balance-label">Wallet balance</div>
            <div className="digital-wallet-balance-value">
              {formatMoney(walletBalance)}
            </div>
          </div>

          <div className="digital-wallet-actions">
            <div
              className="digital-wallet-action"
              onClick={() => setFundsModalMode("add")}
            > {translateLabel("txt_admin_top_up", "Top Up")} </div>
            <div
              className="digital-wallet-action"
              onClick={() => setFundsModalMode("withdraw")}
            > {translateLabel("txt_admin_withdrawal", "Withdrawal")} </div>
          </div>
        </div>

        <div className="digital-wallet-tabs">
          <div
            className={`digital-wallet-tab ${
              activeTab === "withdrawals" ? "digital-wallet-tab-active" : ""
            }`}
            onClick={() => setActiveTab("withdrawals")}
          > {translateLabel("txt_admin_withdrawal_request", "Withdrawal Request")} </div>
          <div
            className={`digital-wallet-tab ${
              activeTab === "transactions" ? "digital-wallet-tab-active" : ""
            }`}
            onClick={() => setActiveTab("transactions")}
          > {translateLabel("txt_business_transaction_history", "Transaction History")} </div>
        </div>
      </div>

      <div className="digital-wallet-requests-section">
        <div
          className={`digital-wallet-filter-row ${
            activeTab === "transactions"
              ? "digital-wallet-filter-row-history"
              : ""
          }`}
        >
          <div className="digital-wallet-search-control">
            <DigitalWalletSearchIcon />
            <input
              className="digital-wallet-search-input"
              placeholder={
                translateText(activeTab === "transactions"
                  ? "Search by name..."
                  : "Search by Request ID...")
              }
            />
          </div>

          <DigitalWalletDropdown
            dropdownKey="types"
            value={selectedType}
            options={typeOptions}
            activeDropdown={activeDropdown}
            onToggle={toggleDropdown}
            onSelect={(option) => selectDropdownOption(option, setSelectedType)}
          />

          {activeTab === "transactions" && (
            <DigitalWalletDropdown
              dropdownKey="transactionTypes"
              value={selectedTransactionType}
              options={transactionTypeOptions}
              activeDropdown={activeDropdown}
              onToggle={toggleDropdown}
              onSelect={(option) =>
                selectDropdownOption(option, setSelectedTransactionType)
              }
            />
          )}

          <div
            className="digital-wallet-filter-control digital-wallet-date-control"
            onClick={() => setDateRangeOpen(true)}
          >
            <DigitalWalletCalendarIcon />
            <div>{dateRangeLabel}</div>
            <DigitalWalletChevronDownIcon />
          </div>
        </div>

        <div className="digital-wallet-table-scroll">
          {activeTab === "withdrawals" && (
            <div className="digital-wallet-table digital-wallet-table-withdrawals">
              <div className="digital-wallet-table-header-row digital-wallet-withdrawal-grid">
                <div>{translateLabel("txt_admin_request_id", "Request ID")}</div>
                <div>{translateLabel("txt_admin_name", "Name")}</div>
                <div>{translateLabel("txt_admin_type", "Type")}</div>
                <div>{translateLabel("txt_admin_amount", "Amount")}</div>
                <div>Date</div>
                <div>{translateLabel("txt_admin_action", "Action")}</div>
              </div>

              {visibleWithdrawalRows.map((request) => (
                <div
                  className="digital-wallet-table-row digital-wallet-withdrawal-grid"
                  key={request.id}
                >
                  <div className="digital-wallet-request-id">{request.id}</div>
                  <div className="digital-wallet-name-cell">
                    <div className="digital-wallet-request-name">
                      {request.name}
                    </div>
                    <div className="digital-wallet-request-phone">
                      {request.phone}
                    </div>
                  </div>
                  <div>
                    <div
                      className={`digital-wallet-type-badge digital-wallet-type-${request.type.toLowerCase()}`}
                    >
                      {request.type}
                    </div>
                  </div>
                  <div className="digital-wallet-amount-cell">
                    {request.amount}
                  </div>
                  <div className="digital-wallet-date-cell">
                    <div>{request.date}</div>
                    <div>{request.time}</div>
                  </div>
                  <div className="digital-wallet-action-cell">
                    <div
                      className="digital-wallet-action-icon digital-wallet-action-reject"
                      onClick={() => {
                        setSelectedRequest(request);
                        setActiveModal("reject");
                      }}
                    >
                      <DigitalWalletRejectIcon />
                    </div>
                    <div
                      className="digital-wallet-action-icon digital-wallet-action-approve"
                      onClick={() => {
                        setSelectedRequest(request);
                        setActiveModal("approve");
                      }}
                    >
                      <DigitalWalletApproveIcon />
                    </div>
                  </div>
                </div>
              ))}

              <div className="digital-wallet-table-footer">
                <div>{translateLabel("txt_admin_showing", "Showing")} {visibleWithdrawalRows.length} requests</div>
                <div className="digital-wallet-pagination">
                  <div className="digital-wallet-pagination-arrow">
                    <DigitalWalletChevronIcon direction="left" />
                  </div>
                  <div className="digital-wallet-pagination-current">1</div>
                  <div className="digital-wallet-pagination-arrow">
                    <DigitalWalletChevronIcon />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "transactions" && (
            <div className="digital-wallet-table digital-wallet-table-history">
              <div className="digital-wallet-table-header-row digital-wallet-history-grid">
                <div>{translateLabel("txt_admin_transaction_id", "Transaction ID")}</div>
                <div>{translateLabel("txt_admin_name", "Name")}</div>
                <div>{translateLabel("txt_admin_type", "Type")}</div>
                <div>{translateLabel("txt_admin_transaction_type", "Transaction Type")}</div>
                <div>{translateLabel("input_business_description", "Description")}</div>
                <div>{translateLabel("txt_admin_amount", "Amount")}</div>
                <div>Date</div>
                <div>{translateLabel("txt_common_status", "Status")}</div>
              </div>

              {visibleTransactionRows.map((transaction) => (
                <div
                  className="digital-wallet-table-row digital-wallet-history-grid"
                  key={transaction.id}
                >
                  <div className="digital-wallet-request-id">
                    {transaction.id}
                  </div>
                  <div className="digital-wallet-name-cell">
                    <div className="digital-wallet-request-name">
                      {transaction.name}
                    </div>
                    <div className="digital-wallet-request-phone">
                      {transaction.phone}
                    </div>
                  </div>
                  <div>
                    <div
                      className={`digital-wallet-type-badge digital-wallet-type-${transaction.type.toLowerCase()}`}
                    >
                      {transaction.type}
                    </div>
                  </div>
                  <div className="digital-wallet-transaction-type-cell">
                    {transaction.transactionType}
                  </div>
                  <div className="digital-wallet-description-cell">
                    {transaction.description}
                  </div>
                  <div
                    className={`digital-wallet-transaction-amount ${
                      transaction.amount.startsWith("+")
                        ? "digital-wallet-transaction-amount-positive"
                        : "digital-wallet-transaction-amount-negative"
                    }`}
                  >
                    {transaction.amount}
                  </div>
                  <div className="digital-wallet-date-cell">
                    <div>{transaction.date}</div>
                    <div>{transaction.time}</div>
                  </div>
                  <div>
                    <div
                      className={`digital-wallet-status-badge digital-wallet-status-${transaction.status.toLowerCase()}`}
                    >
                      {transaction.status}
                    </div>
                  </div>
                </div>
              ))}

              <div className="digital-wallet-table-footer digital-wallet-table-footer-history">
                <div>{translateLabel("txt_admin_showing", "Showing")} {transactionRows.length} requests</div>
                <div className="digital-wallet-pagination">
                  <div className="digital-wallet-pagination-arrow">
                    <DigitalWalletChevronIcon direction="left" />
                  </div>
                  <div className="digital-wallet-pagination-current">1</div>
                  <div className="digital-wallet-pagination-arrow">
                    <DigitalWalletChevronIcon />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DateRangeFilter
        visible={dateRangeOpen}
        value={dateRange}
        onApply={setDateRange}
        onClose={() => setDateRangeOpen(false)}
      />

      <WalletFundsModal
        visible={Boolean(fundsModalMode)}
        mode={fundsModalMode}
        onClose={closeFundsModal}
        onConfirm={handleFundsConfirm}
      />

      {activeModal && (
        <div className="digital-wallet-modal-overlay" onClick={closeModal}>
          <div
            className="digital-wallet-modal-card"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="digital-wallet-modal-close" onClick={closeModal}>
              x
            </div>

            <div
              className={`digital-wallet-modal-status digital-wallet-modal-status-${modalCopy.tone}`}
            >
              {activeModal === "approve" ? (
                <DigitalWalletApproveIcon />
              ) : (
                <DigitalWalletRejectIcon />
              )}
            </div>

            <div className="digital-wallet-modal-title">{translateText(modalCopy.title)}</div>
            {modalCopy.subtitle && (
              <div className="digital-wallet-modal-subtitle">
                {translateText(modalCopy.subtitle)}
              </div>
            )}
            {modalCopy.label && (
              <div className="digital-wallet-modal-label">
                {translateText(modalCopy.label)}
              </div>
            )}

            <textarea
              className="digital-wallet-modal-textarea"
              placeholder={translateText(modalCopy.placeholder)}
            />

            <div className="digital-wallet-modal-actions">
              <div
                className="digital-wallet-modal-action digital-wallet-modal-cancel"
                onClick={closeModal}
              > {translateLabel("btn_cancel", "Cancel")} </div>
              <div
                className={`digital-wallet-modal-action digital-wallet-modal-confirm digital-wallet-modal-confirm-${modalCopy.tone}`}
                onClick={handleWithdrawalDecision}
              >
                {modalCopy.confirmLabel}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DigitalWallet;
